import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@/components/Breadcrumb";
import CreateAnalysisModal from "@/components/CreateAnalysisModal";
import SampleOriginalFilesCard from "@/components/SampleOriginalFilesCard";
import FireExperimentSection from "@/components/samples/FireExperimentSection";
import { useAnalysesBySample, useCreateAnalysis } from "@/hooks/useAnalysis";
import { useEvidenceFiles } from "@/hooks/useEvidenceFiles";
import { useSampleDetail, useUploadSampleFiles } from "@/hooks/useSamples";
import type { AnalysisType, FileUploadResponse } from "@/types";
import { apiErrorDetail } from "@/utils/apiError";
import {
  sampleStatusColor,
  sampleStatusLabels,
  sampleTypeLabels,
} from "@/utils/labels";

function formatFileSize(bytes: number | null): string {
  if (bytes == null) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function SampleDetailPage() {
  const { id } = useParams();
  const sampleId = Number(id);
  const navigate = useNavigate();

  const { data: sample, isLoading, error } = useSampleDetail(sampleId);
  const { data: analyses } = useAnalysesBySample(sampleId);
  // 아래 "파일명" 표시를 정직하게 만들기 위해서도 쓴다 — 상세 설명은 그 자리에.
  const { data: evidenceFiles } = useEvidenceFiles(sampleId);
  // 되찾을 수 있는 원본이 하나도 없는가. 상태를 아직 모르면(로딩·실패)
  // 아무 말도 덧붙이지 않는다 — 모르는 것을 단정하지 않기 위해서다.
  // 기본정보의 "등록된 파일명" 에 무엇을 덧붙일지.
  //
  // ⚠ downloadable 로 판정하면 안 된다 — lost·not_a_file·out_of_scope·
  //   never_stored 가 전부 downloadable:false 라 **네 가지 사실이 한 줄로
  //   합쳐진다.** 그러면 소실된 원본(prod 66·67·70)에 "이름만 기록됨 —
  //   원본 미보관"(= 바이트는 처음부터 없었다)이라고 적히고, 바로 아래
  //   카드는 같은 파일을 "소실"이라고 말해 한 화면이 스스로 모순된다.
  //   증거 소실(사고 보고 대상)이 데이터 미수집(정상)으로 읽히게 된다.
  const originalStatus: "unknown" | "never_stored" | "lost" | "present" =
    (() => {
      const files = evidenceFiles?.files;
      // 빈 배열이면 .every() 가 true 라 단정하게 된다 — 모른다는 뜻이지
      // 없다는 뜻이 아니다.
      if (!files || files.length === 0) return "unknown";
      if (files.some((f) => f.state === "available")) return "present";
      if (files.every((f) => f.state === "never_stored")) return "never_stored";
      if (files.some((f) => f.state === "lost")) return "lost";
      // not_a_file / out_of_scope — 서버가 "읽지 않았다"고 답한 상태다.
      return "unknown";
    })();

  const createAnalysis = useCreateAnalysis();
  const uploadFiles = useUploadSampleFiles();
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [ticFile, setTicFile] = useState<File | null>(null);
  const [msFile, setMsFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<FileUploadResponse | null>(
    null,
  );

  const handleCreateAnalysis = (data: {
    sample_id: number;
    analysis_type: AnalysisType;
  }) => {
    setAnalysisError(null);
    createAnalysis.mutate(data, {
      onSuccess: (analysis) => {
        setShowAnalysisModal(false);
        navigate(`/analyses/${analysis.id}/dashboard`);
      },
      // onError 가 없으면 서버가 거절해도 화면에 아무 일도 일어나지 않는다 —
      // 사용자는 버튼이 고장난 줄 알고 연타한다. 409(이미 진행 중)의 사유를
      // 그대로 보여 준다.
      onError: (err) => {
        setAnalysisError(
          apiErrorDetail(
            err,
            "분석 생성에 실패했습니다. 잠시 후 다시 시도하세요.",
          ),
        );
      },
    });
  };

  // 가장 최근 완료된 분석 (없으면 가장 최근 분석)
  const latestAnalysis = analyses?.length
    ? [...analyses].sort((a, b) =>
        (b.created_at ?? "").localeCompare(a.created_at ?? ""),
      )[0]
    : null;
  const hasCompletedAnalysis = !!analyses?.some(
    (a) => a.status === "completed",
  );

  const handleUploadBoth = () => {
    if (!sample || (!ticFile && !msFile)) return;
    uploadFiles.mutate(
      {
        id: sample.id,
        ticFile: ticFile ?? undefined,
        msFile: msFile ?? undefined,
      },
      {
        onSuccess: (data) => {
          setUploadResult(data);
          setTicFile(null);
          setMsFile(null);
          alert(
            `파일 업로드 완료 — TIC ${data.tic_point_count ?? 0} points, MS ${data.ms_scan_count ?? 0} scans`,
          );
        },
        onError: (err) => {
          alert(
            `업로드 실패: ${err instanceof Error ? err.message : String(err)}`,
          );
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6">
          <Link
            to="/samples"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            &larr; 뒤로
          </Link>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-3" />
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-3" />
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-3" />
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-3" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6">
          <Link
            to="/samples"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            &larr; 뒤로
          </Link>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 font-medium">
            시료 데이터를 불러오는 중 오류가 발생했습니다.
          </p>
          <p className="text-red-500 text-sm mt-1">
            {(error as Error).message}
          </p>
        </div>
      </div>
    );
  }

  if (!sample) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6">
          <Link
            to="/samples"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            &larr; 뒤로
          </Link>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-700 font-medium">
            시료를 찾을 수 없습니다.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* 상단 네비게이션 */}
      <div className="mb-6 flex items-center justify-between">
        <Breadcrumb
          items={[
            { label: "대시보드", to: "/" },
            { label: "시료관리", to: "/samples" },
            { label: sample.name ?? `시료 #${id}` },
          ]}
        />
        <div className="flex items-center gap-2">
          {hasCompletedAnalysis && latestAnalysis && (
            <button
              type="button"
              onClick={() =>
                navigate(`/analyses/${latestAnalysis.id}/dashboard`)
              }
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              분석 대시보드 열기
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              // 모달을 여는 시점에 이전 실패 사유를 지운다 — 남겨 두면 아무
              // 시도도 하지 않았는데 에러가 떠 있다 (SVD-212-09 리뷰).
              setAnalysisError(null);
              setShowAnalysisModal(true);
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              hasCompletedAnalysis
                ? "bg-white text-green-700 border border-green-600 hover:bg-green-50"
                : "bg-green-600 text-white hover:bg-green-700"
            }`}
          >
            {hasCompletedAnalysis ? "재분석 시작" : "분석 시작"}
          </button>
        </div>
      </div>

      {/* 시료 정보 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {sample.name}
            </h1>
            <p className="text-sm text-gray-500">시료 ID: {sample.sample_id}</p>
          </div>
          <span
            className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${sampleStatusColor(sample.status)}`}
          >
            {sampleStatusLabels[sample.status] ?? sample.status}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              시료 유형
            </h3>
            <p className="text-gray-900">
              {sampleTypeLabels[sample.sample_type] ?? sample.sample_type}
            </p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">사건 ID</h3>
            <p className="text-gray-900">
              {sample.case_id != null ? (
                <Link
                  to={`/cases/${sample.case_id}`}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {sample.case_id}
                </Link>
              ) : (
                "-"
              )}
            </p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              등록된 파일명
            </h3>
            <p className="text-gray-900">
              {sample.file_name || "-"}
              {/*
                samples.file_name 은 "등록 당시 원본이 무엇이라 불렸는가"의
                기록일 뿐, 그 파일이 지금 있다는 뜻이 아니다. prod 실측
                (2026-09-07): file_name 이 채워진 시료 132개 중 원본이 실제로
                남아 있는 것은 4개뿐이다 — 나머지 128개는 적재기가 이름만
                기록하고 바이트는 버렸다. 이름만 보여 주면 화면이 없는 파일을
                있는 것처럼 말하게 된다.
              */}
              {sample.file_name && originalStatus === "never_stored" && (
                <span className="ml-1 text-sm text-gray-500">
                  (이름만 기록됨 — 원본 미보관)
                </span>
              )}
              {sample.file_name && originalStatus === "lost" && (
                <span className="ml-1 text-sm text-red-700">
                  (등록 후 소실 — 아래 참조)
                </span>
              )}
            </p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              파일 크기
            </h3>
            <p className="text-gray-900">{formatFileSize(sample.file_size)}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              채취 일시
            </h3>
            <p className="text-gray-900">
              {sample.collection_datetime
                ? new Date(sample.collection_datetime).toLocaleString("ko-KR")
                : "-"}
            </p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              채취 장소
            </h3>
            <p className="text-gray-900">{sample.collection_location || "-"}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              채취 방법
            </h3>
            <p className="text-gray-900">{sample.collection_method || "-"}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">상태</h3>
            <p className="text-gray-900">
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${sampleStatusColor(sample.status)}`}
              >
                {sampleStatusLabels[sample.status] ?? sample.status}
              </span>
            </p>
          </div>
          <div className="md:col-span-2">
            <h3 className="text-sm font-medium text-gray-500 mb-1">설명</h3>
            <p className="text-gray-900 whitespace-pre-wrap">
              {sample.description || "-"}
            </p>
          </div>
        </div>
      </div>

      {/* 화재시험 조건 — 입력된 시료가 모의현장 DB 에 든다 (PLAN 2-11) */}
      <FireExperimentSection sampleId={sample.id} />

      {/* 스펙트럼 파일 업로드 카드 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          스펙트럼 파일 업로드
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          GC-MS 분석을 위한 TIC와 MS 파일을 업로드하세요.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* TIC 파일 */}
          <div>
            <label
              htmlFor="tic-file-input"
              className="text-sm font-medium text-gray-700 mb-1 block"
            >
              TIC 파일 (.csv / .txt)
            </label>
            <input
              id="tic-file-input"
              type="file"
              accept=".csv,.txt"
              onChange={(e) => setTicFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 border border-gray-300 rounded-lg p-1.5 cursor-pointer"
            />
            {ticFile && (
              <p className="text-xs text-green-700 mt-1">
                &#10003; {ticFile.name}
              </p>
            )}
          </div>

          {/* MS 파일 */}
          <div>
            <label
              htmlFor="ms-file-input"
              className="text-sm font-medium text-gray-700 mb-1 block"
            >
              MS 파일 (.csv / .txt)
            </label>
            <input
              id="ms-file-input"
              type="file"
              accept=".csv,.txt"
              onChange={(e) => setMsFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 border border-gray-300 rounded-lg p-1.5 cursor-pointer"
            />
            {msFile && (
              <p className="text-xs text-green-700 mt-1">
                &#10003; {msFile.name}
              </p>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={handleUploadBoth}
          disabled={(!ticFile && !msFile) || uploadFiles.isPending}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {uploadFiles.isPending ? "업로드 중..." : "파일 업로드"}
        </button>

        {uploadResult && (
          <div className="mt-3 text-xs text-gray-600 bg-green-50 border border-green-200 rounded-lg p-3">
            업로드 완료 — TIC {uploadResult.tic_point_count ?? 0} points
            &middot; MS {uploadResult.ms_scan_count ?? 0} scans
          </div>
        )}

        {sample.file_name && (
          <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-600">
              등록된 파일명:{" "}
              <span className="font-medium">{sample.file_name}</span>
              {sample.file_size != null &&
                ` (${formatFileSize(sample.file_size)})`}
              {originalStatus === "never_stored" && (
                <span className="text-gray-500">
                  {" "}
                  — 원본 파일은 저장된 적이 없습니다.
                </span>
              )}
              {originalStatus === "lost" && (
                <span className="text-red-700">
                  {" "}
                  — 등록된 원본이 소실되었습니다.
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* 증거 원본 — 업로드 카드의 거울상 (2026-09-07 T2) */}
      <SampleOriginalFilesCard sampleId={sample.id} />

      {/* 분석 생성 모달 */}
      <CreateAnalysisModal
        sampleId={sample.id}
        sampleName={sample.name}
        isOpen={showAnalysisModal}
        onClose={() => {
          setShowAnalysisModal(false);
          setAnalysisError(null);
        }}
        onSubmit={handleCreateAnalysis}
        isLoading={createAnalysis.isPending}
        error={analysisError}
      />
    </div>
  );
}
